import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TitleHeading from 'app_previous/molecules/Shared/TitleHeading/TitleHeading';
import {
  FixedTableOfContentsContainer,
  LoadingContainer,
  ShareLinksContainer,
  SpacingRelativeContainer,
  TableContentContainer,
  tableOfContentsTopPosition,
} from './FixedTableOfContents.styles';
import { TableOfContent } from 'src/functions/getTableOfContentDictionary';
import Anchor from './Anchor';
import { topBarHeightDesktop } from 'src/functions/topBarHelper';
import {
  FacebookShareLink,
  TwitterShareLink,
  LinkedInShareLink,
} from 'app_previous/molecules/Article/SocialMedia/SocialMediaLinks';
import { palette } from 'app_previous/styles/colors';
import LoadingDots from 'app_previous/molecules/Standard/LoadingDots/LoadingDots';

interface FixedTableOfContentsProps {
  tocData: TableOfContent[];
  className?: string;
  isSticky?: boolean;
  isLoading?: boolean;
  pathPrefix?: string;
  articleData: {
    slug: string;
    title: string;
  };
  attachedElement?: () => JSX.Element;
}

const FixedTableOfContents = ({
  tocData = [],
  isSticky,
  articleData,
  className = '',
  isLoading = false,
  pathPrefix = 'resources',
  attachedElement,
}: FixedTableOfContentsProps): JSX.Element => {
  const spacingContainerRef = useRef<HTMLDivElement>(null);
  const fixedContainerRef = useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [isPastContainer, setIsPastContainer] = useState(false);
  const [currentSelectedAnchorId, setCurrentSelectedAnchorId] = useState<string | null>(null);

  const anchors = useMemo(() => {
    if (!tocData?.length) return [];

    const anchorElements: Element[] = [];

    const loadElements = (content: TableOfContent) => {
      anchorElements.push(document.querySelector(`#${content.id}`));

      if (content.children) {
        content.children.forEach(loadElements);
      }
    };

    tocData.forEach(loadElements);

    return anchorElements;
  }, [tocData]);

  const handleAnchorPositionTracking = useCallback(() => {
    if (!isSticky) return;

    const currentAnchor = anchors.reduce((prev, current) => {
      const topPosition = current?.getBoundingClientRect().top;
      const headlineTopPaddingCompensation = 40 + topBarHeightDesktop;

      if (!topPosition || topPosition >= headlineTopPaddingCompensation) return prev;

      if (!prev || topPosition > prev?.getBoundingClientRect().top) {
        prev = current;
      }

      return prev;
    }, null);

    if (!currentAnchor) {
      setCurrentSelectedAnchorId(null);
      return;
    }

    const currentAnchorId = currentAnchor.id;
    setCurrentSelectedAnchorId(currentAnchorId);
  }, [anchors, isSticky]);

  const handleFixedPositionScroll = () => {
    if (!fixedContainerRef.current) return;

    const fixedContainerRect = fixedContainerRef.current.getBoundingClientRect();
    const spacingContainerRect = spacingContainerRef.current.getBoundingClientRect();

    setIsPastContainer(
      fixedContainerRect.height + tableOfContentsTopPosition - spacingContainerRect.height > spacingContainerRect.y
    );
    setIsFixed(spacingContainerRect.y < tableOfContentsTopPosition);
  };

  const handleResize = () => {
    if (!fixedContainerRef.current) return;

    handleFixedPositionScroll();
    fixedContainerRef.current.style.left = `${spacingContainerRef?.current?.getBoundingClientRect().left}px`;
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !isSticky) return;

    handleResize();
    handleFixedPositionScroll();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleFixedPositionScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleFixedPositionScroll);
    };
  }, []);

  useEffect(() => {
    if (!isSticky) return;

    window.addEventListener('scroll', handleAnchorPositionTracking);

    return () => window.removeEventListener('scroll', handleAnchorPositionTracking);
  }, [handleAnchorPositionTracking, isSticky]);

  const shareLinkProps = {
    path: `/${pathPrefix}/${articleData.slug}`,
    title: articleData.title,
  };

  return (
    <SpacingRelativeContainer
      ref={spacingContainerRef}
      className={`fixed-table-of-contents-container ${className}`}
    >
      <FixedTableOfContentsContainer
        ref={fixedContainerRef}
        isFixed={isFixed}
        isPastContainer={isPastContainer}
      >
        <TableContentContainer>
          <TitleHeading
            title={`IN THIS ${pathPrefix === 'case-studies' ? 'STORY' : 'ARTICLE'}`}
            size='0.875rem'
          />
          {isLoading && (
            <LoadingContainer>
              <LoadingDots
                loading
                background='white'
                loadingDotColor='deepPurple'
              />
            </LoadingContainer>
          )}
          <ul style={{ listStyle: 'none', margin: '0', paddingLeft: '0' }}>
            {tocData.map((link, index) => (
              <li key={index}>
                <p>
                  <Anchor
                    href={`#${link.id}`}
                    selected={link.id === currentSelectedAnchorId}
                  >
                    {link.content}
                  </Anchor>
                </p>
                {link.children && (
                  <ul style={{ listStyle: 'none' }}>
                    {link.children.map((element, indexChildren) => (
                      <li key={`${index}-${indexChildren}`}>
                        <p>
                          <Anchor
                            selected={element.id === currentSelectedAnchorId}
                            href={`#${element.id}`}
                          >
                            {element.content}
                          </Anchor>
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </TableContentContainer>
        <ShareLinksContainer>
          <TitleHeading
            title='SHARE'
            size='0.875rem'
            color={palette.greyText}
          />
          <FacebookShareLink
            {...shareLinkProps}
            color={'greyText'}
          />
          <TwitterShareLink
            {...shareLinkProps}
            color={'greyText'}
          />
          <LinkedInShareLink
            {...shareLinkProps}
            color={'greyText'}
          />
        </ShareLinksContainer>
        {attachedElement && attachedElement()}
      </FixedTableOfContentsContainer>
    </SpacingRelativeContainer>
  );
};

export default FixedTableOfContents;
